import axios from 'axios';

// Engine Configuration - All available engines for each service type
export const AI_ENGINES = {
  TEXT_PROCESSING: [
    {
      id: 'darb-text',
      name: 'Darb Text Processing',
      url: 'https://server.alfanar-int.com/darb-text',
    },
    // Add more text processing engines here
  ],
  TTS: [
    {
      id: 'darb-tts',
      name: 'Darb TTS',
      url: 'https://server.alfanar-int.com/darb-tts',
    },
    // Add more TTS engines here
  ],
  STT: [
    {
      id: 'stt-server',
      name: 'STT Server',
      url: 'https://server.alfanar-int.com/stt',
    },
    // Add more STT engines here
  ],
  IMAGE_PROCESSING: [
    {
      id: 'picTotext-server',
      name: 'PicToText Server',
      url: 'http://ocr.aiastra.io',
    },
    // Add more image processing engines here
  ],
};

// Default engine IDs
const DEFAULT_ENGINES = {
  TEXT_PROCESSING: 'darb-text',
  TTS: 'darb-tts',
  STT: 'stt-server',
  IMAGE_PROCESSING: 'picTotext-server',
};

// AI Microservices Configuration
const AI_CONFIG = {
  TIMEOUT: 10000, // 10 seconds timeout for text and TTS
  STT_TIMEOUT: 120000, // 120 seconds (2 minutes) timeout for audio transcription
};

export interface AITextResponse {
  id: string;
  msg: string | number; // Can be a number (numid) or string
  numids?: number[]; // Optional array of numids
  stage?: string; // Optional stage
  completed?: boolean; // Optional completion status
}

export interface AITTSResponse {
  status?: string;
  timestamp?: string;
  text?: string;
  text_length?: number;
  language?: string;
  audio_format?: string;
  format?: string; // Some APIs use 'format' instead of 'audio_format'
  audio_base64: string;
  message?: string;
}

export interface AITextRequest {
  id: string;
  msg: string;
  isCall?: boolean;
}

export interface AITTSRequest {
  text: string | number; // Can be text or numid (number)
  slow?: boolean;
}

export interface AISTTResponse {
  text: string;
  language: string;
  duration?: number;
  segments_count?: number;
  processing_time?: number;
}

export interface AISTTRequest {
  id: string;
  file: File;
}

export interface AIVINExtractionResponse {
  ok: boolean;
  text: string;
  text_length: number;
  avg_confidence_pct: number;
  elapsed_ms: number;
  fields: {
    vin?: string;
    plate_no?: string;
    engine_capacity_cc_guess?: string;
    dates_found?: string[];
  };
  langs_used: string[];
  lines: Array<{
    confidence: number;
    polygon: number[][];
    text: string;
  }>;
}

export interface AIPicToTextResponse {
  text?: string;
  status?: string;
  message?: string;
  error?: string;
  [key: string]: any; // Allow for flexible response structure
}

class AIService {
  private selectedEngines: {
    TEXT_PROCESSING: string;
    TTS: string;
    STT: string;
    IMAGE_PROCESSING: string;
  } = {
    TEXT_PROCESSING: DEFAULT_ENGINES.TEXT_PROCESSING,
    TTS: DEFAULT_ENGINES.TTS,
    STT: DEFAULT_ENGINES.STT,
    IMAGE_PROCESSING: DEFAULT_ENGINES.IMAGE_PROCESSING,
  };

  constructor() {
    // Load saved engine preferences from localStorage
    this.loadEnginePreferences();
  }

  /**
   * Load engine preferences from localStorage
   */
  private loadEnginePreferences() {
    try {
      const saved = localStorage.getItem('ai_engine_preferences');
      if (saved) {
        const preferences = JSON.parse(saved);
        this.selectedEngines = { ...this.selectedEngines, ...preferences };
      }
    } catch (error) {
      console.warn('Failed to load engine preferences:', error);
    }
  }

  /**
   * Save engine preferences to localStorage
   */
  private saveEnginePreferences() {
    try {
      localStorage.setItem('ai_engine_preferences', JSON.stringify(this.selectedEngines));
    } catch (error) {
      console.warn('Failed to save engine preferences:', error);
    }
  }

  /**
   * Set the engine for a specific service type
   */
  setEngine(serviceType: 'TEXT_PROCESSING' | 'TTS' | 'STT' | 'IMAGE_PROCESSING', engineId: string) {
    if (AI_ENGINES[serviceType].find(e => e.id === engineId)) {
      this.selectedEngines[serviceType] = engineId;
      this.saveEnginePreferences();
      return true;
    }
    return false;
  }

  /**
   * Get the current engine for a service type
   */
  getEngine(serviceType: 'TEXT_PROCESSING' | 'TTS' | 'STT' | 'IMAGE_PROCESSING') {
    return this.selectedEngines[serviceType];
  }

  /**
   * Get the URL for the current engine of a service type
   */
  private getEngineUrl(serviceType: 'TEXT_PROCESSING' | 'TTS' | 'STT' | 'IMAGE_PROCESSING'): string {
    const engineId = this.selectedEngines[serviceType];
    const engine = AI_ENGINES[serviceType].find(e => e.id === engineId);
    return engine?.url || AI_ENGINES[serviceType][0].url;
  }

  /**
   * Create axios client dynamically based on selected engine
   */
  private createClient(serviceType: 'TEXT_PROCESSING' | 'TTS' | 'STT' | 'IMAGE_PROCESSING') {
    const url = this.getEngineUrl(serviceType);
    const isMultipart = serviceType === 'STT' || serviceType === 'IMAGE_PROCESSING';
    const timeout = serviceType === 'STT' ? AI_CONFIG.STT_TIMEOUT : 
                    serviceType === 'IMAGE_PROCESSING' ? 60000 : AI_CONFIG.TIMEOUT;

    return axios.create({
      baseURL: url,
      timeout,
      headers: {
        'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
      },
    });
  }

  /**
   * Send message to AI text processing service
   * @param userId - Unique user identifier
   * @param message - User message
   * @param isCall - Whether this is a call context (default: false)
   * @returns AI response
   */
  async processText(userId: string, message: string, isCall: boolean = false): Promise<AITextResponse> {
    try {
      const requestData: AITextRequest = {
        id: userId,
        msg: message,
        isCall: isCall,
      };

      const client = this.createClient('TEXT_PROCESSING');
      console.log('🤖 Text Processing Request:', { 
        userId, 
        message: message.substring(0, 100) + '...',
        isCall,
        engine: this.selectedEngines.TEXT_PROCESSING,
        url: this.getEngineUrl('TEXT_PROCESSING')
      });
      const response = await client.post('/msg/', requestData);
      const msgValue = response.data.msg;
      const msgDisplay = typeof msgValue === 'string' 
        ? (msgValue.length > 100 ? msgValue.substring(0, 100) + '...' : msgValue)
        : String(msgValue);
      console.log('🤖 Text Processing Response:', { 
        id: response.data.id, 
        msg: msgDisplay,
        msgType: typeof msgValue,
        msgLength: typeof msgValue === 'string' ? msgValue.length : undefined,
        numids: response.data.numids,
        stage: response.data.stage
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Text Processing Error:', error);
      if (error.response) {
        console.error('❌ Text Processing Error Response:', error.response.data);
      }
      
      // Return fallback response
      return {
        id: userId,
        msg: '❌ عذراً، حدث خطأ في معالجة الرسالة. يرجى المحاولة مرة أخرى.',
      };
    }
  }

  /**
   * Convert text to speech using TTS service
   * @param textOrNumid - Text to convert or numid (number) to convert
   * @param slow - Whether to use slow speech
   * @returns TTS response with base64 audio
   */
  async convertToSpeech(textOrNumid: string | number, slow: boolean = false): Promise<AITTSResponse | null> {
    try {
      // TTS service expects numid as a number, so convert to string if it's a number
      const textValue = typeof textOrNumid === 'number' ? String(textOrNumid) : textOrNumid;
      
      const requestData: AITTSRequest = {
        text: textValue,
        slow,
      };

      const client = this.createClient('TTS');
      const ttsUrl = this.getEngineUrl('TTS');
      console.log('🎵 TTS Request:', { 
        text: textValue,
        textType: typeof textOrNumid,
        slow,
        engine: this.selectedEngines.TTS,
        baseUrl: ttsUrl,
        endpoint: '/api/tts/convert',
        fullUrl: `${ttsUrl}/api/tts/convert`
      });
      
      const response = await client.post('/api/tts/convert', requestData);
      console.log('🎵 TTS Response:', response.data);
      console.log('🎵 TTS Response keys:', Object.keys(response.data || {}));
      
      // Handle different response formats
      if (response.data && response.data.audio_base64) {
        // Map response to our interface format
        const ttsResponse: AITTSResponse = {
          audio_base64: response.data.audio_base64,
          format: response.data.format || response.data.audio_format || 'wav',
          language: response.data.language || 'ar',
          text: textValue,
          text_length: textValue.length,
          status: 'success',
          audio_format: response.data.format || response.data.audio_format || 'wav'
        };
        
        console.log('🎵 TTS Success:', {
          hasAudio: !!ttsResponse.audio_base64,
          audioLength: ttsResponse.audio_base64.length,
          format: ttsResponse.format,
          language: ttsResponse.language
        });
        
        return ttsResponse;
      } else {
        console.error('❌ TTS API Error: No audio_base64 in response');
        console.error('❌ TTS Response data:', response.data);
        return null;
      }
    } catch (error: any) {
      console.error('❌ TTS Error:', error);
      if (error.response) {
        console.error('❌ TTS Error Response:', error.response.data);
        console.error('❌ TTS Error Status:', error.response.status);
        console.error('❌ TTS Error Headers:', error.response.headers);
      }
      if (error.request) {
        console.error('❌ TTS Request Error:', error.request);
      }
      return null;
    }
  }

  /**
   * Convert speech to text using STT service
   * @param audioFile - Audio file to convert
   * @param userId - User identifier (optional for this service)
   * @returns STT response with transcribed text
   */
  async convertToText(audioFile: File, userId?: string): Promise<AISTTResponse | null> {
    try {
      const formData = new FormData();
      formData.append('file', audioFile);

      const client = this.createClient('STT');
      console.log('🎤 STT Request:', { 
        userId, 
        fileName: audioFile.name, 
        fileSize: audioFile.size,
        fileType: audioFile.type,
        engine: this.selectedEngines.STT,
        url: this.getEngineUrl('STT')
      });
      
      const response = await client.post('/transcribe', formData);
      console.log('🎤 STT Response:', response.data);
      
      // Check if response has text (the actual indicator of success)
      if (response.data && response.data.text) {
        return response.data;
      } else {
        console.error('❌ STT API Error: No text in response');
        return null;
      }
    } catch (error: any) {
      console.error('❌ STT Error:', error);
      if (error.response) {
        console.error('❌ STT Error Response:', error.response.data);
      }
      return null;
    }
  }

  /**
   * Process WhatsApp message with AI and optionally generate voice response
   * @param userId - User identifier
   * @param message - User message
   * @param generateVoice - Whether to generate voice response
   * @returns Object with text response and optional audio
   */
  async processWhatsAppMessage(
    userId: string,
    message: string,
    generateVoice: boolean = false
  ): Promise<{
    textResponse: AITextResponse;
    audioResponse?: AITTSResponse;
  }> {
    try {
      // Get text response from AI
      const textResponse = await this.processText(userId, message);

      let audioResponse: AITTSResponse | undefined;

      // Generate voice response if requested
      // Note: If msg is a number (numid), TTS won't work properly without the actual answer text
      // The caller should resolve the numid to text before calling this with generateVoice=true
      if (generateVoice && textResponse.msg) {
        const textForTTS = typeof textResponse.msg === 'number' 
          ? String(textResponse.msg) 
          : textResponse.msg;
        const ttsResult = await this.convertToSpeech(textForTTS);
        audioResponse = ttsResult || undefined;
      }

      return {
        textResponse,
        audioResponse,
      };
    } catch (error: any) {
      console.error('WhatsApp AI Processing Error:', error);
      
      // Return fallback response
      return {
        textResponse: {
          id: userId,
          msg: '❌ عذراً، حدث خطأ في معالجة الرسالة. يرجى المحاولة مرة أخرى.',
        },
      };
    }
  }

  /**
   * Check if AI services are available
   * @returns Promise<boolean>
   */
  async checkServicesHealth(): Promise<{
    textProcessing: boolean;
    tts: boolean;
    stt: boolean;
  }> {
    const health = {
      textProcessing: false,
      tts: false,
      stt: false,
    };

    try {
      // Check text processing service
      console.log('🔍 Checking Text Processing service...');
      const client = this.createClient('TEXT_PROCESSING');
      const response = await client.get('/health');
      console.log('🔍 Text Processing Response:', { 
        status: response.status, 
        data: response.data,
        headers: response.headers 
      });
      health.textProcessing = true;
      console.log('✅ Text Processing service is available');
    } catch (error: any) {
      console.warn('❌ Text processing service not available:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        code: error.code
      });
    }

    try {
      // Check TTS service - use the root endpoint
      console.log('🔍 Checking TTS service...');
      const client = this.createClient('TTS');
      const ttsUrl = this.getEngineUrl('TTS');
      console.log('🔍 TTS URL:', ttsUrl);
      const response = await client.get('/');
      console.log('🔍 TTS Response Details:', { 
        status: response.status, 
        statusText: response.statusText,
        data: response.data,
        dataType: typeof response.data,
        dataLength: response.data?.length,
        headers: response.headers,
        config: {
          baseURL: response.config.baseURL,
          url: response.config.url,
          method: response.config.method
        }
      });
      
      const responseText = String(response.data);
      // Check for various possible success indicators
      const hasExpectedText = responseText.includes('TTS') || 
                              responseText.includes('Multi-language') ||
                              responseText.includes('Text-to-Speech');
      
      console.log('🔍 TTS Response Analysis:', {
        responseText: responseText,
        hasExpectedText: hasExpectedText,
        isStatus200: response.status === 200,
        dataExists: !!response.data
      });
      
      if (response.status === 200 && response.data) {
        health.tts = true;
        console.log('✅ TTS service is available');
      } else {
        console.warn('❌ TTS service response unexpected:', {
          status: response.status,
          data: response.data
        });
      }
    } catch (error: any) {
      console.warn('❌ TTS service not available:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        code: error.code,
        config: error.config ? {
          baseURL: error.config.baseURL,
          url: error.config.url,
          method: error.config.method
        } : 'No config'
      });
    }

    try {
      // Check STT service
      console.log('🔍 Checking STT service...');
      const client = this.createClient('STT');
      const response = await client.get('/');
      console.log('🔍 STT Response:', { 
        status: response.status, 
        data: response.data,
        headers: response.headers 
      });
      health.stt = true;
      console.log('✅ STT service is available');
    } catch (error: any) {
      console.log('🔍 STT Error Details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        code: error.code
      });
      // STT service returns 405 (Method Not Allowed) for GET requests, but that means it's running
      if (error.response && (error.response.status === 404 || error.response.status === 405)) {
        health.stt = true;
        console.log('✅ STT service is available (404/405 response indicates service is running)');
      } else {
        console.warn('❌ STT service not available');
      }
    }

    return health;
  }

  /**
   * Extract text from image using picTotext-server
   * @param imageFile - Image file to process
   * @returns Picture-to-text response with extracted text
   */
  async extractTextFromImage(imageFile: File): Promise<AIPicToTextResponse | null> {
    try {
      const formData = new FormData();
      // Use 'image' field name as that's what the extract_text endpoint expects
      formData.append('image', imageFile);

      const client = this.createClient('IMAGE_PROCESSING');
      const engineId = this.selectedEngines.IMAGE_PROCESSING;
      const engineUrl = this.getEngineUrl('IMAGE_PROCESSING');
      
      console.log('📷 Picture-to-Text Request:', { 
        fileName: imageFile.name, 
        fileSize: imageFile.size,
        fileType: imageFile.type,
        engine: engineId,
        baseUrl: engineUrl,
        endpoint: '/extract_text',
        fullUrl: `${engineUrl}/extract_text`
      });
      
      // Use the specific endpoint: /extract_text
      const response = await client.post('/extract_text', formData);
      console.log('📷 Picture-to-Text Response:', response.data);
      console.log('📷 Picture-to-Text Response keys:', Object.keys(response.data || {}));
      
      if (response.data) {
        // Check if response has 'text' field directly or nested
        const responseData = response.data;
        if (responseData.text) {
          return responseData;
        } else if (responseData.fields && responseData.fields.vin) {
          // If it's a VIN extraction response, extract the text from fields
          return {
            text: responseData.fields.vin,
            ...responseData
          };
        } else {
          // Try to find text in any field
          const textField = responseData.text || responseData.extracted_text || responseData.result;
          if (textField) {
            return {
              text: textField,
              ...responseData
            };
          }
        }
        return responseData;
      } else {
        console.error('❌ Picture-to-Text API Error: Invalid response format');
        return null;
      }
    } catch (error: any) {
      console.error('❌ Picture-to-Text Error:', error);
      if (error.response) {
        console.error('❌ Picture-to-Text Error Response:', error.response.data);
        console.error('❌ Picture-to-Text Error Status:', error.response.status);
        console.error('❌ Picture-to-Text Error Headers:', error.response.headers);
      }
      if (error.request) {
        console.error('❌ Picture-to-Text Request Error:', error.request);
      }
      return null;
    }
  }

  /**
   * Extract text and VIN from image using VIN extraction service
   * @param imageFile - Image file to process
   * @returns VIN extraction response with text and VIN if found
   */
  async extractVINFromImage(imageFile: File): Promise<AIVINExtractionResponse | null> {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const client = this.createClient('IMAGE_PROCESSING');
      console.log('🔍 VIN Extraction Request:', { 
        fileName: imageFile.name, 
        fileSize: imageFile.size,
        fileType: imageFile.type,
        engine: this.selectedEngines.IMAGE_PROCESSING,
        url: this.getEngineUrl('IMAGE_PROCESSING')
      });
      
      const response = await client.post('/extract_text', formData);
      console.log('🔍 VIN Extraction Response:', response.data);
      
      if (response.data && response.data.ok !== undefined) {
        return response.data;
      } else {
        console.error('❌ VIN Extraction API Error: Invalid response format');
        return null;
      }
    } catch (error: any) {
      console.error('❌ VIN Extraction Error:', error);
      if (error.response) {
        console.error('❌ VIN Extraction Error Response:', error.response.data);
      }
      return null;
    }
  }

  /**
   * Get service configuration
   * @returns Service configuration
   */
  getConfig() {
    return AI_CONFIG;
  }

  /**
   * Get all available engines
   */
  getAvailableEngines() {
    return AI_ENGINES;
  }

  /**
   * Get current engine selections
   */
  getSelectedEngines() {
    return { ...this.selectedEngines };
  }
}

export default new AIService();

